/* eslint-disable react/no-unknown-property */
import { getUserInfoApi, updateUserInfoApi } from '@/app/csr-src/apis/personal/index.api';
import { imageUploadApi } from '@/common-components-src/api/image/index.api';
import CDNImage from '@/components/cdnImage/cdnImage';
import LoadingIcon from '@/components/loadingIcon';
import UKToast from '@/components/UKToast/index';
import IMAGEAPI_TYPE from '@/constants/imageApiType';
import { useTranslation } from '@/i18n/client';
import { getI18nLang } from '@/i18n/settings';
import { convertDate, dateFormat } from '@/utils/helpers';
import { PROFILE_EVENT_NAMES, profileBusinessLogger } from '@/utils/logger/instance/profileLogger';
import Cancel from '@mui/icons-material/Cancel';
import {
  Box,
  Button,
  DialogActions,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import 'dayjs/locale/de'; // 德语
import 'dayjs/locale/en'; // 英文
import 'dayjs/locale/fr'; // 法语
import 'dayjs/locale/nl'; // 荷兰语
import 'dayjs/locale/zh-cn'; // 导入中文语言包
import 'dayjs/locale/zh-tw'; // 中文繁体
import React, { useEffect, useState } from 'react';
import style from '../index.module.scss';
import { UserInfoT } from '../types/index';
import { BoxStyle, InputStyle, avatarUploadIcon } from './style';
interface ContentProps {
  onContentClose?: (v: boolean) => void;
  saverSuccessFn?: () => void;
}

const getDateFormat = (language: string) => {
  const formatMap: { [key: string]: string } = {
    zh: 'DD MM月 YYYY',
    'zh-CN': 'DD MM月 YYYY',
    'zh-TW': 'DD MM月 YYYY',
    'zh-HK': 'DD MM月 YYYY',
    zh_CN: 'DD MM月 YYYY',
    zh_TW: 'DD MM月 YYYY',
    zh_HK: 'DD MM月 YYYY',
    fr: 'DD MMM YYYY',
    'fr-FR': 'DD MMM YYYY',
    fr_FR: 'DD MMM YYYY',
    nl: 'DD MMM YYYY',
    'nl-NL': 'DD MMM YYYY',
    nl_NL: 'DD MMM YYYY',
    de: 'DD. MMM YYYY',
    'de-DE': 'DD. MMM YYYY',
    de_DE: 'DD. MMM YYYY',
    en: 'D MMM YYYY',
    'en-GB': 'D MMM YYYY',
    en_GB: 'D MMM YYYY',
    'en-US': 'D MMM YYYY',
    en_US: 'D MMM YYYY',
  };
  return formatMap[language] || 'D MMM YYYY';
};

const ContentBox = (props: ContentProps) => {
  const { t, i18n } = useTranslation();
  const { onContentClose, saverSuccessFn } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [birthday, setBirthday] = useState<any>(''); // 生日选择
  const [error, setError] = useState<string | null>('');
  const [avatar, setAvatar] = useState<string | null>(''); // 头像图片
  const [username, setUsername] = useState<string | null>(''); // 用户名选输入
  const [gender, setGender] = useState<string | number | null>(''); // 性别
  useEffect(() => {
    // 动态设置 dayjs 语言环境
    dayjs.locale(getI18nLang());
    getUserInfoFn();
  }, []);

  // 获取用户信息
  const getUserInfoFn = async () => {
    const [err, res] = await getUserInfoApi();
    if (res && res.success) {
      const data: UserInfoT = res.data; // 用户信息
      setGender(data.gender); // 性别
      setAvatar(data.avatar); // 头像图片
      setUsername(data.nickName);
      setBirthday(data.birthdayTimestamp ? convertDate(data.birthdayTimestamp) : '');
    }
  };
  // 头像上传
  const handleAvatarChange = async (event: any) => {
    const file = event.target.files[0];
    if (file) {
      if (file && file.size > 5242880) {
        UKToast.show({
          icon: 'error',
          content: t('upload.imageMaximum'),
        });
        return;
      }

      imageUploadApi(file, IMAGEAPI_TYPE.PersonalInfo).then((res: any) => {
        if (res && res.msg) {
          setAvatar(res.msg); // 头像图片
        }
      });
    }
  };
  // 清空 用户名
  const closeUserName = () => {
    setError('');
    setUsername('');
  };
  // 用户名称 change
  const handleUsernameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value.length > 50) {
      return;
    }
    setError('');
    setUsername(value);
  };
  // 点击保存按钮
  const SaverBtn = async () => {
    if (username) {
      if (nickNameTest(username)) {
        updateUserInfoFn();
      } else {
        setError(t('settings.profileInfo.yourContainsCharacters'));
      }
    } else {
      updateUserInfoFn();
    }
  };
  const nickNameTest = (nickname: string) => {
    const regex = /^(?=.*[^\s])(?!.*admin)[\w\s.\u0080-\uFFFF]{4,50}$/;
    if (!regex.test(nickname)) {
      return false;
    }
    return true;
  };
  // 修改用户信息；
  const updateUserInfoFn = async () => {
    const data: any = {};
    gender && (data.gender = gender);
    if (gender == 0) {
      data.gender = gender;
    }
    avatar && (data.avatar = avatar);
    birthday && (data.birthday = birthday);
    username && (data.nickName = username);
    setIsLoading(true);
    try {
      const [err, res] = await updateUserInfoApi(data);
      if (res && res.success) {
        UKToast.show({
          content: res?.msg || t('settings.profileInfo.successfully'),
          icon: 'success',
        });
        saverSuccessFn && saverSuccessFn();
        setIsLoading(false);
      } else {
        UKToast.show({
          content: res?.msg,
          icon: 'error',
        });
        setIsLoading(false);
        try {
          profileBusinessLogger({
            eventName: PROFILE_EVENT_NAMES.EDIT_PROFILE_SAVE_FAILURE,
            message: {
              msg: res?.msg,
              profile: data,
            },
          });
        } catch (error) {}
      }
    } catch (error) {
      console.error('Error:', error);
      setIsLoading(false);
    }
  };
  // 关闭弹框
  const handleClose = () => {
    onContentClose && onContentClose(false);
  };

  return (
    <div className={style.personalInfoSetDialogContent}>
      <div className={style.assetsBox}>
        <CDNImage
          className={style.assetsBox__image}
          src={avatar || ''}
          errorImgType={1}
          width={120}
          height={120}
          alt={'Avatar'}
        />
        <div className={style.assetsTakePictures}>
          <input
            className={style.imageInput}
            accept='image/*'
            id='avatar-upload'
            onChange={handleAvatarChange}
            type='file'
          />
          <div className={style.uploadIcon}>{avatarUploadIcon}</div>
        </div>
      </div>

      <div className={style.FormBox}>
        <Box sx={BoxStyle}>
          <TextField
            fullWidth
            multiline
            size='small'
            margin='normal'
            name='userName'
            label={t('settings.profileInfo.userName')}
            variant='outlined'
            value={username}
            error={!!error}
            helperText={error}
            maxRows={5}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleUsernameChange(e)}
            aria-describedby='custom-css-outlined-input'
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment
                    position='end'
                    sx={{ '& .MuiIconButton-root': { right: '-10px' } }}
                  >
                    {username ? (
                      <>
                        <IconButton
                          onClick={closeUserName}
                          sx={{
                            '& .MuiSvgIcon-root': { padding: 0 },
                          }}
                        >
                          <Cancel sx={{ fontSize: 18 }} />
                        </IconButton>
                      </>
                    ) : null}
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              ...InputStyle,
              '&.MuiInputBase-input': {
                minHeight: 72, // Set a minimum height for the input field
              },
            }}
          />
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale={t('settings.profileInfo.datePickerLanguage')}
          >
            <DatePicker
              sx={{
                ...InputStyle,
                width: '100%',
                margin: '6px 0',
                '& .MuiInputBase-root': {
                  width: '100% !important',
                  color: 'info',
                },
              }}
              slotProps={{
                textField: {
                  inputProps: {
                    disabled: true, // 禁止手动输入，但允许点击弹出选择器
                  },
                },
              }}
              label={t('settings.profileInfo.birthDate')}
              format={getDateFormat(i18n.language)}
              views={['year', 'month', 'day']}
              value={birthday ? dayjs(birthday) : null}
              disableFuture
              onChange={(newValue: any) => {
                setBirthday(dateFormat(newValue));
              }}
            />
          </LocalizationProvider>

          {/* 性别选项 */}
          <FormControl fullWidth margin='normal' variant='outlined' size='small' sx={InputStyle}>
            <InputLabel id='gender-select-label'>{t('settings.profileInfo.gender')}</InputLabel>
            <Select
              labelId='gender-select-label'
              name='gender'
              label={t('settings.profileInfo.gender')}
              value={gender}
              onChange={(event) => {
                console.log(event.target.value, 'gender');
                setGender(event.target.value);
              }}
            >
              <MenuItem value={1}>{t('settings.profileInfo.male')}</MenuItem>
              <MenuItem value={2}>{t('settings.profileInfo.female')}</MenuItem>
              <MenuItem value={3}>{t('settings.profileInfo.other')}</MenuItem>
              <MenuItem value={0}>{t('settings.profileInfo.preferNotToSay')}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </div>
      <DialogActions
        sx={{ padding: '20px 0', width: '100%', display: 'flex', justifyContent: 'right' }}
      >
        <Button
          variant='contained'
          disableElevation
          onClick={handleClose}
          sx={{
            width: '94px',
            height: '32px',
            backgroundColor: '#00000000', // 自定义背景颜色
            color: '#1a1a1a', // 自定义文字颜色
            textTransform: 'none',
            boxShadow: 'none',
            border: '1px solid #E0E1E5',
            '&:hover': {
              backgroundColor: '#E0E1E5', // 悬停时的背景颜色
            },
          }}
        >
          {t('settings.common.cancel')}
        </Button>
        <Button
          variant='contained'
          disableElevation
          onClick={SaverBtn}
          disabled={isLoading || !username}
          startIcon={isLoading ? <LoadingIcon /> : null}
          sx={{
            width: '82px',
            height: '32px',
            backgroundColor: 'var(--sys-color-Primary700)', // 自定义背景颜色
            color: '#fff', // 自定义文字颜色
            textTransform: 'none',
            boxShadow: 'none',
            '&.Mui-disabled': {
              backgroundColor: '#FF9FA7',
              color: '#fff',
            },
          }}
        >
          {t('settings.common.save')}
        </Button>
      </DialogActions>
    </div>
  );
};

export default ContentBox;
